/**
 * Cart Popup Component
 * Custom cart popup that appears below navbar when user adds item from product grid
 * 
 * @version 1.0.0
 * @author Fade Shopify Team
 */

class CartPopup extends HTMLElement {
  constructor() {
    super();
    
    this.isOpen = false;
    this.autoCloseTimer = null;
    this.cart = null;
    this.headerObserver = null;
  }

  connectedCallback() {
    this.attachEventListeners();
    this.initHeaderTracking();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.cleanupHeaderTracking();
  }

  /**
   * Initialize header height tracking
   * Automatically updates cart popup position based on header height
   */
  initHeaderTracking() {
    this.updateCartPopupPosition();
    
    // Track header height changes using ResizeObserver
    const header = document.querySelector('sticky-header, .header-wrapper');
    if (header) {
      this.headerObserver = new ResizeObserver(() => {
        this.updateCartPopupPosition();
      });
      this.headerObserver.observe(header);
    }
    
    // Also update on scroll (for sticky header transitions)
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
    window.addEventListener('resize', this.handleResize.bind(this), { passive: true });
  }

  /**
   * Cleanup header tracking observers
   */
  cleanupHeaderTracking() {
    if (this.headerObserver) {
      this.headerObserver.disconnect();
      this.headerObserver = null;
    }
    window.removeEventListener('scroll', this.handleScroll.bind(this));
    window.removeEventListener('resize', this.handleResize.bind(this));
  }

  /**
   * Update cart popup top position based on actual header height
   */
  updateCartPopupPosition() {
    const header = document.querySelector('sticky-header, .header-wrapper');
    if (header) {
      const headerHeight = header.offsetHeight;
      this.style.top = `${headerHeight}px`;
      console.log(`Cart popup positioned at ${headerHeight}px (header height)`);
    }
  }

  /**
   * Handle scroll events (for sticky header transitions)
   */
  handleScroll() {
    this.updateCartPopupPosition();
  }

  /**
   * Handle window resize events
   */
  handleResize() {
    this.updateCartPopupPosition();
  }

  attachEventListeners() {
    // Listen for cart updates from product grid and product form
    document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));

    // Close button
    const closeBtn = this.querySelector('.cart-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.close.bind(this));
    }

    // Click outside to close
    document.addEventListener('click', this.handleClickOutside.bind(this));

    // ESC key to close
    document.addEventListener('keydown', this.handleEscKey.bind(this));

    // Listen for navbar cart icon click
    const cartIconBtn = document.querySelector('.header__icon--cart-popup');
    if (cartIconBtn) {
      cartIconBtn.addEventListener('click', this.handleCartIconClick.bind(this));
    }
  }

  removeEventListeners() {
    document.removeEventListener('cart:updated', this.handleCartUpdate.bind(this));
    document.removeEventListener('cartdrawer:opened', this.close.bind(this));
    document.removeEventListener('click', this.handleClickOutside.bind(this));
    document.removeEventListener('keydown', this.handleEscKey.bind(this));
  }

  async handleCartIconClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isOpen) {
      this.close();
      return;
    }

    try {
      this.cart = await this.fetchCart();
      this.renderCart();
      this.open();
    } catch (error) {
      console.error('Error opening cart:', error);
    }
  }

  async handleCartUpdate(event) {
    console.log('Cart updated event received:', event.detail);
    
    try {
      // Fetch latest cart data
      this.cart = await this.fetchCart();
      
      // Render cart items
      this.renderCart();
      
      // Open popup
      this.open();
      
    } catch (error) {
      console.error('Error handling cart update:', error);
      this.showError('Failed to load cart');
    }
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  }

  renderCart() {
    if (!this.cart) return;

    // Update item count
    const itemCount = this.cart.item_count;
    const countElement = this.querySelector('.cart-popup__count');
    if (countElement) {
      countElement.textContent = `${itemCount} ${itemCount === 1 ? 'Item' : 'Items'}`;
    }

    // Render items
    const itemsContainer = this.querySelector('.cart-popup__items');
    if (itemsContainer) {
      itemsContainer.innerHTML = this.cart.items.map(item => this.renderItem(item)).join('');
      
      // Attach event listeners to quantity buttons and delete buttons
      this.attachItemEventListeners();
    }

    // Update subtotal
    this.updateSubtotal();

    // Update header cart icon bubble
    this.updateCartIconBubble();
  }

  updateCartIconBubble() {
    if (!this.cart) return;
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
    if (bubble) {
      bubble.textContent = this.cart.item_count;
    }
    const bubbleContainer = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (bubbleContainer) {
      bubbleContainer.style.display = this.cart.item_count > 0 ? '' : 'none';
    }
  }

  renderItem(item) {
    const imageUrl = item.image || item.featured_image?.url || '';
    const title = item.product_title || item.title;
    const variant = this.formatVariantTitle(item.variant_title) || 'No variant';
    const quantity = item.quantity;
    const price = item.final_price;
    const totalPrice = item.final_line_price;
    const variantId = item.variant_id;

    return `
      <div class="cart-popup__item" data-variant-id="${variantId}">
        <div class="cart-item__left">
          <div class="cart-item__image">
            ${imageUrl ? `<img src="${imageUrl}" alt="${title}" loading="lazy">` : ''}
          </div>
          <div class="cart-item__info">
            <div class="cart-item__text-wrapper">
              <h3 class="cart-item__title">${title}</h3>
              <p class="cart-item__variant" style="white-space: pre-line;">${variant}</p>
            </div>
            <div class="cart-item__quantity">
              <button class="quantity-btn quantity-btn--minus" data-variant-id="${variantId}" ${quantity <= 1 ? 'disabled' : ''}>
                -
              </button>
              <input type="number" class="quantity-input" value="${quantity}" min="1" readonly>
              <button class="quantity-btn quantity-btn--plus" data-variant-id="${variantId}">
                +
              </button>
            </div>
          </div>
        </div>
        <div class="cart-item__right">
          <button class="cart-item__delete" data-variant-id="${variantId}" aria-label="Remove ${title}">
            <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.2 3.1H10.8M4.8 5.5V9.1M7.2 5.5V9.1M2.4 3.1L3 10.9C3 11.2 3.3 11.5 3.6 11.5H8.4C8.7 11.5 9 11.2 9 10.9L9.6 3.1M4.2 3.1V1.9C4.2 1.6 4.5 1.3 4.8 1.3H7.2C7.5 1.3 7.8 1.6 7.8 1.9V3.1" stroke="#BEBEBE" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="cart-item__price-wrapper">
            <p class="cart-item__total">${this.formatMoney(totalPrice)}</p>
            <p class="cart-item__each">@ ${this.formatMoney(price)}</p>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Format variant title with labels
   * Converts "Single (Device Only) / Pink" to "Type: Single (Device Only)\nColor: Pink"
   */
  formatVariantTitle(variantTitle) {
    if (!variantTitle) return '';
    
    // Split by " / " separator (Shopify's default format)
    const parts = variantTitle.split(' / ');
    
    // Hardcoded labels for product variants
    // Add more labels here if product has more than 2 options
    const labels = ['Type', 'Color', 'Size'];
    
    // Format each part with label and colon
    return parts.map((part, index) => {
      const label = labels[index] || `Option ${index + 1}`;
      return `${label}: ${part}`;
    }).join('\n'); // Newline for multi-line display
  }

  attachItemEventListeners() {
    // Quantity buttons
    const minusButtons = this.querySelectorAll('.quantity-btn--minus');
    const plusButtons = this.querySelectorAll('.quantity-btn--plus');
    const deleteButtons = this.querySelectorAll('.cart-item__delete');

    minusButtons.forEach(btn => {
      btn.addEventListener('click', this.handleQuantityChange.bind(this, 'minus'));
    });

    plusButtons.forEach(btn => {
      btn.addEventListener('click', this.handleQuantityChange.bind(this, 'plus'));
    });

    deleteButtons.forEach(btn => {
      btn.addEventListener('click', this.handleDeleteItem.bind(this));
    });
  }

  async handleQuantityChange(action, event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    const itemElement = button.closest('.cart-popup__item');
    const quantityInput = itemElement.querySelector('.quantity-input');
    let currentQuantity = parseInt(quantityInput.value);

    // Calculate new quantity
    let newQuantity = action === 'plus' ? currentQuantity + 1 : currentQuantity - 1;
    
    if (newQuantity < 1) return; // Prevent negative quantity

    try {
      // Show loading state
      button.disabled = true;
      itemElement.classList.add('is-loading');

      // Update cart via AJAX
      await this.updateCartQuantity(variantId, newQuantity);

      // Refresh cart
      this.cart = await this.fetchCart();
      this.renderCart();

    } catch (error) {
      console.error('Error updating quantity:', error);
      this.showError('Failed to update quantity');
    } finally {
      button.disabled = false;
      itemElement.classList.remove('is-loading');
    }
  }

  async handleDeleteItem(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    const itemElement = button.closest('.cart-popup__item');

    try {
      // Show loading state
      button.disabled = true;
      itemElement.classList.add('is-removing');

      // Remove item (set quantity to 0)
      await this.updateCartQuantity(variantId, 0);

      // Refresh cart
      this.cart = await this.fetchCart();
      
      // If cart is empty, close popup
      if (this.cart.item_count === 0) {
        this.close();
      } else {
        this.renderCart();
      }

    } catch (error) {
      console.error('Error deleting item:', error);
      this.showError('Failed to remove item');
      button.disabled = false;
      itemElement.classList.remove('is-removing');
    }
  }

  async updateCartQuantity(variantId, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating cart quantity:', error);
      throw error;
    }
  }

  updateSubtotal() {
    if (!this.cart) return;

    const subtotalElement = this.querySelector('.cart-popup__subtotal-value');
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(this.cart.total_price);
    }
  }

  formatMoney(cents) {
    // Format as Rupiah
    const amount = cents / 100;
    return `Rp. ${amount.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  open() {
    this.isOpen = true;
    this.classList.add('is-open');
    
    // Prevent body scroll (optional)
    // document.body.style.overflow = 'hidden';
    
    console.log('Cart popup opened');
  }

  close() {
    this.isOpen = false;
    this.classList.remove('is-open');
    
    // Restore body scroll
    // document.body.style.overflow = '';
    
    // Clear auto-close timer
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    
    console.log('Cart popup closed');
  }

  handleClickOutside(event) {
    if (!this.isOpen) return;

    // Don't close if clicking the cart icon button (it has its own toggle)
    const cartIconBtn = document.querySelector('.header__icon--cart-popup');
    if (cartIconBtn && cartIconBtn.contains(event.target)) return;

    // Check if click is outside popup
    const container = this.querySelector('.cart-popup__container');
    if (container && !container.contains(event.target)) {
      this.close();
    }
  }

  handleEscKey(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  showError(message) {
    console.error('Cart popup error:', message);
    // TODO: Implement error UI
  }
}

// Register custom element
customElements.define('cart-popup', CartPopup);
